import type { RawMqttMessage } from '@/lib/mqtt/types';
import { cn } from '@/lib/utils';
import { memo } from 'react';
import type { DsBadge } from '../types';
import { buildPreview } from '../utils/highlight';

const BADGE_CLASS: Record<DsBadge, string> = {
  telemetry: 'text-(--color-green) bg-[rgba(29,122,64,.12)]',
  surveillance: 'text-[#2563eb] bg-[rgba(37,99,235,.12)]',
  traffic: 'text-(--color-amber) bg-[rgba(245,158,11,.12)]',
  system: 'text-(--color-text3) bg-[rgba(107,114,128,.12)]',
};

const NO_TERMS: string[] = [];

interface Props {
  msg: RawMqttMessage;
  badge: DsBadge;
  selected: boolean;
  /** Stable store action — keep the reference stable so React.memo can skip
   *  unchanged rows. The per-row closure is created here, inside the memoized
   *  component, so it doesn't break the parent's prop comparison. */
  onSelect: (id: string) => void;
  /** Substrings to mark. Must be a stable reference (memoized upstream) or
   *  every row re-renders on every frame. */
  terms?: readonly string[];
}

export const MessageRow = memo(function MessageRow({
  msg,
  badge,
  selected,
  onSelect,
  terms = NO_TERMS,
}: Props) {
  const ts = new Date(msg.receivedAt).toISOString().replace('T', ' ').substring(0, 23);
  const preview = buildPreview(msg.raw, terms);
  return (
    <button
      type="button"
      onClick={() => onSelect(msg.id)}
      className={cn(
        'block w-full cursor-pointer border-b border-(--color-border-lite) px-2.5 py-[5px] text-left transition-colors hover:bg-(--color-surface)',
        selected && 'bg-(--color-green-bg) border-l-2 border-l-(--color-green)',
      )}
    >
      <div className="mb-0.5 flex items-center justify-between font-mono text-[0.769em] text-(--color-text3)">
        <span>{ts}</span>
        <span className="rounded border border-(--color-border) bg-(--color-surface) px-1 py-px text-[0.9em]">
          QoS {msg.qos}
        </span>
      </div>
      <div className="overflow-hidden text-ellipsis whitespace-nowrap font-mono text-[0.846em] text-(--color-text2)">
        <span
          className={cn(
            'mr-1 inline-block rounded px-[5px] py-px font-sans text-[0.82em] font-bold',
            BADGE_CLASS[badge],
          )}
        >
          {badge}
        </span>
        {preview.map((seg, i) =>
          seg.hit ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional slices of one string; there is no stabler key.
            <mark key={i} className="ds-hit">
              {seg.text}
            </mark>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: see above.
            <span key={i}>{seg.text}</span>
          ),
        )}
      </div>
    </button>
  );
});
